import { ChangeDetectorRef, Component, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TippyModule } from '@ngneat/helipopper';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { TruncateTextPipe } from '@shared/pipes';
import { IconComponent } from '@shared/components/ui';
import { CalendarEventItem } from '@core/types';
import { DateTimeUtility } from '@core/utils';
import { DialogRef, DialogService } from '@ngneat/dialog';
import { CalendarState, CalendarViewService } from '@core/services';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
  selector: 'app-calendar-view',
  imports: [CommonModule, NgxSkeletonLoaderModule, TruncateTextPipe, TippyModule, IconComponent, TranslateModule],
  templateUrl: './calendar-view.component.html',
  styleUrl: './calendar-view.component.scss',
})
export class CalendarViewComponent implements OnInit, OnChanges {
  @Input() calendarItems: CalendarEventItem[] = [];
  @Input() isLoading = false;
  @Input() startOfWeek: 'monday' | 'sunday' = 'monday';
  @Input() fixedMonthNumber?: number;
  @Input() fixedYear?: number;
  @Input() useExternalDateSelection = false;
  @Input() initialDate?: Date;
  @Output() eventClicked = new EventEmitter<CalendarEventItem>();
  @Output() dayClicked = new EventEmitter<Date>();
  currentDate = new Date();
  currentMonthYear = '';
  calendarDays: {
    day: number;
    isCurrentMonth: boolean;
    isToday: boolean;
    isWeekend: boolean;
    events: CalendarEventItem[];
  }[] = [];
  weekDayObjects: { name: string; number: number }[] = [];
  private readonly _translateService = inject(TranslateService);
  private readonly _dialogService = inject(DialogService);
  private readonly _calendarService = inject(CalendarViewService);
  private readonly _cdr = inject(ChangeDetectorRef);

  constructor() {
    this.weekDayObjects = DateTimeUtility.getWeekDays(this._translateService, this.startOfWeek);
  }

  get dayNames(): string[] {
    return this.weekDayObjects.map((w) => w.name);
  }

  ngOnInit(): void {
    this._updateWeekDays();

    // Initialize service only if using external date selection
    if (!this.useExternalDateSelection) {
      this._calendarService.initialize(this.initialDate, this.fixedMonthNumber, this.fixedYear);
    }

    // Subscribe to date changes from service
    this._calendarService.currentState$.pipe(untilDestroyed(this)).subscribe({
      next: (state: CalendarState) => {
        this.currentDate = new Date(state.currentDate);
        const monthName = DateTimeUtility.getMonthName(state.currentDate.getMonth() + 1, this._translateService);
        this.currentMonthYear = `${monthName} ${state.currentDate.getFullYear()}`;
        this._generateCalendarDays();
      },
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['startOfWeek']) {
      this._updateWeekDays();
      this._generateCalendarDays();
    }

    if (changes['calendarItems']) {
      this._generateCalendarDays();
    }

    // Update service if fixed inputs change
    if (changes['fixedMonthNumber'] || changes['fixedYear'] || changes['initialDate']) {
      // Use setTimeout to avoid ExpressionChangedAfterItHasBeenCheckedError
      setTimeout(() => {
        const hasMonth = this.fixedMonthNumber !== undefined && this.fixedMonthNumber !== null;
        const hasYear = this.fixedYear !== undefined && this.fixedYear !== null;

        if (hasMonth && hasYear) {
          // Both month and year provided
          this._calendarService.setMonthYear(this.fixedMonthNumber, this.fixedYear);
        } else if (hasMonth) {
          // Only month provided, keep current year
          this._calendarService.setMonth(this.fixedMonthNumber);
        } else if (hasYear) {
          // Only year provided, keep current month
          this._calendarService.setYear(this.fixedYear);
        } else if (this.initialDate) {
          // Use initial date
          this._calendarService.updateDate(this.initialDate);
        }
      }, 0);
    }
  }

  // Internal navigation (when useExternalDateSelection is false)
  navigateMonth(delta: number): void {
    this._calendarService.navigateMonth(delta);
  }

  getEventsForDate(day: number): CalendarEventItem[] {
    const year = this.currentDate.getFullYear();
    const month0 = this.currentDate.getMonth();
    const isoMidnight = new Date(Date.UTC(year, month0, day)).toISOString();
    const dateKey = DateTimeUtility.getDateOnly(isoMidnight);
    return this.calendarItems.filter((ev) => ev.date === dateKey);
  }

  getEventClass(type: string): string {
    switch (type) {
      case 'press':
        return 'event-press';
      case 'meeting':
        return 'event-meeting';
      case 'training':
        return 'event-training';
      case 'urgent':
        return 'event-urgent';
      default:
        return 'event-default';
    }
  }

  getVisibleEvents(events: CalendarEventItem[]): CalendarEventItem[] {
    return events.slice(0, 3);
  }

  hasMoreEvents(events: CalendarEventItem[]): boolean {
    return events.length > 3;
  }

  getRemainingEventsCount(events: CalendarEventItem[]): number {
    return Math.max(0, events.length - 3);
  }

  showAllEvents(dayData: any): void {
    this._dialogService.open(CalendarEventsDialogComponent, {
      size: 'md',
      closeButton: false,
      data: {
        events: dayData.events,
        dayNumber: dayData.day,
        monthYear: this.currentMonthYear,
        onEventClick: (event: CalendarEventItem) => this.eventClicked.emit(event),
      },
    });
  }

  onDayCellClick(dayData: any): void {
    if (dayData.events && dayData.events.length > 0) return;

    const year = this.currentDate.getFullYear();
    const month = this.currentDate.getMonth();

    let clickedDate: Date;

    if (dayData.isCurrentMonth) {
      // Current month day
      clickedDate = new Date(year, month, dayData.day);
    } else {
      // Previous or next month day
      const dayIndex = this.calendarDays.findIndex((d) => d === dayData);

      if (dayIndex < 15) {
        // Previous month
        clickedDate = new Date(year, month - 1, dayData.day);
      } else {
        // Next month
        clickedDate = new Date(year, month + 1, dayData.day);
      }
    }
    this.dayClicked.emit(clickedDate);
  }

  private _updateWeekDays(): void {
    this.weekDayObjects = DateTimeUtility.getWeekDays(this._translateService, this.startOfWeek);
  }

  private _getDaysInMonth(date: Date): number {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  }

  private _getFirstDayOfMonth(date: Date): number {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  }

  private _generateCalendarDays(): void {
    const year = this.currentDate.getFullYear();
    const month0 = this.currentDate.getMonth();
    const today = new Date();
    const todayYear = today.getFullYear();
    const todayMonth = today.getMonth();
    const todayDate = today.getDate();

    const daysInMonth = this._getDaysInMonth(this.currentDate);
    const jsFirstDayOfMonth = this._getFirstDayOfMonth(this.currentDate);
    const firstDayIndex = this.weekDayObjects.findIndex((w) => w.number === jsFirstDayOfMonth);

    const days: {
      day: number;
      isCurrentMonth: boolean;
      isToday: boolean;
      isWeekend: boolean;
      events: CalendarEventItem[];
    }[] = [];

    // Prepend previous-month days
    for (let i = 0; i < firstDayIndex; i++) {
      const prevDate = new Date(year, month0, -firstDayIndex + i + 1);
      const weekday = prevDate.getDay();
      days.push({
        day: prevDate.getDate(),
        isCurrentMonth: false,
        isToday: false,
        isWeekend: weekday === 0 || weekday === 6,
        events: [],
      });
    }

    // Add current-month days
    for (let d = 1; d <= daysInMonth; d++) {
      const isToday = year === todayYear && month0 === todayMonth && d === todayDate;
      const thisDate = new Date(year, month0, d);
      const weekday = thisDate.getDay();
      days.push({
        day: d,
        isCurrentMonth: true,
        isToday,
        isWeekend: weekday === 0 || weekday === 6,
        events: this.getEventsForDate(d),
      });
    }

    // Append next-month days to fill 42 cells
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      const nextDate = new Date(year, month0 + 1, i);
      const weekday = nextDate.getDay();
      days.push({
        day: i,
        isCurrentMonth: false,
        isToday: false,
        isWeekend: weekday === 0 || weekday === 6,
        events: [],
      });
    }

    this.calendarDays = days;
  }
}

// Dialog Component for showing all events
@Component({
  selector: 'app-calendar-events-dialog',
  imports: [CommonModule, IconComponent, TranslateModule],
  template: `
    <div class="dialog">
      <header class="dialog__header">
        <h3 class="dialog__header__title">Events {{ 'RRULE.for' | translate }} {{ data.dayNumber.toString().padStart(2, '0') }} {{ data.monthYear }}</h3>
        <div class="dialog__header__action-buttons">
          <div (click)="closeDialog()" class="dialog__header__action-buttons__button">
            <span>
              <app-icon [cssStyle]="{ 'margin-top': '3px' }" iconName="close" size="14px"></app-icon>
            </span>
          </div>
        </div>
      </header>

      <div class="dialog__content">
        @for (event of data.events; track event) {
          <div [ngClass]="getEventClass(event.type)" class="dialog-event-item" (click)="onEventClick(event)">
            <div class="event-time">{{ event.time ? (event.time | date: 'HH:mm') : (event.startTime | date: 'HH:mm') }}</div>
            <div class="event-title">{{ event.title }}</div>
          </div>
        }
      </div>
    </div>
  `,
  styles: [
    `
      .dialog-event-item {
        font-size: 14px;
        padding: 12px 16px;
        border-radius: 8px;
        text-align: left;
        display: flex;
        gap: 12px;
        font-weight: 600;
        cursor: pointer;
        transition:
          transform 0.2s,
          box-shadow 0.2s;
        margin-bottom: 8px;

        &:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        }

        .event-time {
          font-weight: 500;
          min-width: 50px;
        }

        .event-title {
          flex: 1;
        }

        &.event-press {
          background-color: #fbbf24;
          color: #92400e;
        }

        &.event-meeting {
          background-color: #ef4444;
          color: white;
        }

        &.event-training {
          background-color: #3b82f6;
          color: white;
        }

        &.event-urgent {
          background-color: #dc2626;
          color: white;
        }

        &.event-default {
          background-color: var(--orange);
          color: var(--text-color-light);
        }
      }

      @media (max-width: 768px) {
        .dialog-event-item {
          font-size: 12px;
          padding: 10px 12px;
        }
      }
    `,
  ],
})
export class CalendarEventsDialogComponent {
  data: {
    events: CalendarEventItem[];
    dayNumber: number;
    monthYear: string;
    onEventClick: (event: CalendarEventItem) => void;
  };
  private readonly _dialogRef = inject(DialogRef);

  constructor() {
    const _dialogRef = this._dialogRef;

    this.data = _dialogRef.data;
  }

  getEventClass(type: string): string {
    switch (type) {
      case 'press':
        return 'event-press';
      case 'meeting':
        return 'event-meeting';
      case 'training':
        return 'event-training';
      case 'urgent':
        return 'event-urgent';
      default:
        return 'event-default';
    }
  }

  onEventClick(event: CalendarEventItem): void {
    this.data.onEventClick(event);
    this.closeDialog();
  }

  closeDialog(): void {
    this._dialogRef.close();
  }
}
